import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import {useQuery} from 'react-query';
import {toast} from 'react-toastify';
import moment from 'moment';
import * as Actions from '../../store/actions';
import {
  DashboardLayout,
  PrimaryButton,
  TableLayout,
  Pagination,
  PageLimit,
  EmptyState,
  SearchInput,
  Loader,
  SecondaryButton,
} from '../../components';
import {
  corruptedFile,
  eye,
  lookupEmpty,
  nigeriaIc,
  southAfricaIc,
} from '../../assets/images/images';
import {PAGES_URL} from '../../helpers/constants';
import {useAllApps} from '../../hooks/useApps';
import {fetchBusinessPool} from '../../requests/queries/business360';
import EasyLookupModals from '../EasyLookup/EasyLookupModals';
import {getIDType} from '../Customer360/LayoutConstants';
import {OverviewCards} from '../../components/OverviewCards';
import {buildTableCardData} from '../../helpers/buildTableCardData';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';
import {dateRanges} from 'helpers/getDateRange';

const tableHeader = [
  {
    label: 'Business Name',
    key: 'business_name',
  },
  {
    label: 'Business ID',
    key: 'business_number',
  },
  {
    label: 'Countries',
    key: 'countries',
  },
  {
    label: 'ID Type',
    key: 'id_type',
  },
  {
    label: 'Date Updated',
    key: 'date_updated',
  },
];

function Business360() {
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [showFilter, setShowFilter] = useState(true);

  const [filterParams, setFilterParams] = useState({
    date: 'seven_days',
    dateRange: dateRanges.seven_days(),
    status: undefined,
  });

  const {userApps} = useAllApps();
  const allApps = userApps?.map(app => app.value)?.join(',');

  const statusFilter = {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [
      {label: 'Valid', value: 'matched'},
      {label: 'Invalid', value: 'mismatched'},
    ],
    multiSelect: false,
  };

  const handleFilterChange = values => {
    setFilterParams({
      dateRange: values.dateRange,
      status: values.status,
    });
    setCurrentPage(1);
  };

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  const {data: businessPool, isLoading} = useQuery(
    [
      'business-pool',
      {page: currentPage, limit, search: searchValue, ...filterParams},
    ],
    () =>
      fetchBusinessPool(allApps, {
        page: currentPage,
        limit,
        search: searchValue,
        ...(filterParams.status && {status: filterParams.status}),
        ...(filterParams.dateRange && {
          date_from: filterParams.dateRange.start,
          date_to: filterParams.dateRange.end,
        }),
      }),
    {
      onSuccess: data => {
        if (data && data.message) {
          toast.success(data.message);
        }
      },
      onError: error => {
        toast.error(
          error?.response?.data?.error || 'Failed to fetch business pool',
        );
      },
      enabled: !!allApps,
      retry: 1,
    },
  );

  const businessPoolData = businessPool?.rows;
  const totalBusinessCount = businessPool?.count;

  const getCountryLogo = country =>
    ({
      nigeria: nigeriaIc,
      ng: nigeriaIc,
      'south-africa': southAfricaIc,
      sa: southAfricaIc,
    }[country?.toLowerCase() || '']);

  const pageClick = selected => {
    setCurrentPage(selected);
  };

  const handleBusinessLookup = verification => {
    navigate(
      PAGES_URL.BUSINESS_360_BUSINESS_LOOKUP.replace(':slug', verification.id),
      {
        state: {
          entityTypes: verification.entity_types
            ? verification.entity_types.split(',')
            : [],
        },
      },
    );
  };

  const handleSearchChange = value => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const tableCardData = buildTableCardData({
    count: totalBusinessCount,
  });

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Business 360</p>
      }
    >
      <EasyLookupModals
        open={openModal}
        setOpen={setOpenModal}
        lookupType="Single"
        batch={false}
        lookupCategory="Business"
        business360={true}
      />

      <div className="flex items-center py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="Search business name or ID"
            onChange={handleSearchChange}
            defaultValue={searchValue}
          />

          <SecondaryButton
            buttonText="Filter"
            onClick={toggleFilterVisibility}
          />
        </div>

        <PrimaryButton
          buttonText="Add Business"
          onClick={() => setOpenModal(!openModal)}
        />
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2
          filters={[statusFilter]}
          onFilterChange={handleFilterChange}
          initialValues={filterParams}
        />
      </div>

      <OverviewCards
        cardData={tableCardData}
        overviewLoading={isLoading}
        className={`grid grid-cols-${
          tableCardData.length > 0 ? tableCardData.length : 1
        } gap-2 lg:gap-4 flex-wrap py-6`}
      />
      <TableLayout negativeMargins>
        <thead className="text-xs font-semibold uppercase">
          <tr className="bg-white80">
            {tableHeader.map(({label, key}) => (
              <th
                key={key}
                className={`${
                  label === 'Business Name'
                    ? 'pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap'
                    : 'pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap'
                }`}
              >
                <div className="flex items-end gap-1 cursor-pointer">
                  <span>{label}</span>
                </div>
              </th>
            ))}
            <th></th>
          </tr>
        </thead>

        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={tableHeader.length + 1}>
                <Loader height={50} />
              </td>
            </tr>
          )}
          {!isLoading && businessPoolData?.length === 0 && (
            <tr>
              <td colSpan={tableHeader.length + 1}>
                <EmptyState
                  body={
                    searchValue || filterParams.status || filterParams.dateRange
                      ? 'This search or filter did not return any data.'
                      : 'Perform business lookups with ease.'
                  }
                  height={35}
                  src={
                    searchValue || filterParams.status || filterParams.dateRange
                      ? corruptedFile
                      : lookupEmpty
                  }
                  bodyMaxWidth="max-w-[250px]"
                  noBtn={
                    !!(
                      searchValue ||
                      filterParams.status ||
                      filterParams.dateRange
                    )
                  }
                  customBtn={
                    !(
                      searchValue ||
                      filterParams.status ||
                      filterParams.dateRange
                    ) ? (
                      <PrimaryButton
                        buttonText="Add Business"
                        className="mt-2 w-fit"
                        onClick={() => setOpenModal(!openModal)}
                      />
                    ) : undefined
                  }
                />
              </td>
            </tr>
          )}
          {!isLoading &&
            businessPoolData &&
            businessPoolData?.map(verification => (
              <tr
                onClick={() => handleBusinessLookup(verification)}
                className="border-b border-[0.5px] cursor-pointer border-[#E4E3EC] hover:bg-gray-50"
                key={verification.id}
              >
                <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                  {verification?.business_name || 'N/A'}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {verification?.business_number || 'N/A'}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  <div className="flex items-center -space-x-[6px]">
                    {verification?.countries &&
                      [verification?.countries]?.map((country, i) => (
                        <div
                          key={i}
                          className="w-5 h-5 flex items-center justify-center bg-blue-50 outline outline-1 outline-blue-100 rounded-full"
                        >
                          <img
                            src={getCountryLogo(country?.toLowerCase())}
                            alt={country || 'country'}
                            width={12}
                            height={8}
                          />
                        </div>
                      ))}
                    {!verification?.countries && 'N/A'}
                  </div>
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {getIDType(verification?.entity_types) || 'N/A'}
                </td>
                <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                  {verification?.updatedAt
                    ? moment(verification?.updatedAt).format(
                        'Do MMM, YYYY h:mm A',
                      )
                    : 'N/A'}
                </td>
                <td className="p-4 pl-0 whitespace-nowrap">
                  <div className="flex items-center gap-4">
                    <button
                      title="View"
                      onClick={e => {
                        e.stopPropagation();
                        navigate(
                          PAGES_URL.BUSINESS_360_BUSINESS_LOOKUP.replace(
                            ':slug',
                            verification.id,
                          ),
                          {
                            state: {
                              entityTypes: verification.entity_types
                                ? verification.entity_types.split(',')
                                : [],
                            },
                          },
                        );
                      }}
                      className="h-4 aspect-square"
                    >
                      <img src={eye} alt="View Details" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </TableLayout>

      {businessPoolData && businessPoolData.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
          <PageNumber
            currentPage={+currentPage}
            limit={limit}
            totalCount={totalBusinessCount}
          />
          <div className="flex items-center gap-5">
            <PageLimit
              onLimitChange={newLimit => {
                setLimit(newLimit);
                setCurrentPage(1);
              }}
              total={totalBusinessCount}
              length={businessPoolData?.length}
              limit={limit}
            />
            <Pagination
              total={
                totalBusinessCount ? Math.ceil(totalBusinessCount / limit) : 0
              }
              current={+currentPage}
              onPageChange={activePage => {
                pageClick(activePage);
              }}
            />
          </div>
        </div>
      )}
      <div
        className="fixed bottom-0 left-0 right-0 z-10 px-4 py-2 overflow-x-hidden sm:hidden bg-white80"
        style={{
          boxShadow: '0px -1px 8px rgba(51, 52, 66, 0.25)',
        }}
      >
        <PrimaryButton
          buttonText="Perform Business Lookup"
          xPadding="p-[35px]"
          onClick={() => setOpenModal(!openModal)}
          className="w-full bottom-2"
        />
      </div>
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(Business360);
